import type { IconName } from "@sagot/ui";
import type { EntitySort, EntityStatusFilter } from "./graph-types";

/** Rows per page of the item list (`GET /v1/graph/entities?limit=`). */
export const GRAPH_PAGE_SIZE = 25;
export const GRAPH_SEARCH_DEBOUNCE_MS = 300;
/** Longest search text the API accepts (`SEARCH_MAX_CHARS`). */
export const GRAPH_SEARCH_MAX_CHARS = 200;
/** Longest name or other name the API accepts (`NAME_MAX_CHARS`). */
export const GRAPH_NAME_MAX_CHARS = 200;
/** Other names one item can hold (`ALIASES_PER_ENTITY_MAX`). */
export const GRAPH_ALIASES_MAX = 20;
/** Characters the API refuses in a name (`NAME_CHARS_MESSAGE`): markup and quoting characters. */
export const GRAPH_NAME_FORBIDDEN = /[<>{}[\]|\\`"]/;
/** What the item's detail lists at most: connections (`DETAIL_RELATIONS_MAX`) and chunk references (`DETAIL_EVIDENCE_MAX`). */
export const GRAPH_RELATIONS_SHOWN = 100;
export const GRAPH_EVIDENCE_SHOWN = 50;
/** Items listed in the merge dialog's picker. */
export const GRAPH_PICKER_SIZE = 8;
/** Run statuses that mean "being read now": the summary is read again every few seconds. */
export const GRAPH_ACTIVE_RUN_STATUSES = ["queued", "running"] as const;
export const GRAPH_POLL_MS = 3000;
/** After a rebuild is accepted the summary is watched this long, even before its run row appears. */
export const GRAPH_WATCH_AFTER_REBUILD_MS = 60_000;
/** The budget meter turns to its warning colour from this share of the month's allowance. */
export const GRAPH_BUDGET_WARN_AT = 0.8;
export const SECONDS_PER_MINUTE = 60;
/** Refusals that mean the page is out of date (the item was merged, hidden or removed elsewhere): the lists are read again. */
export const GRAPH_STALE_ERROR_CODES: readonly string[] = ["graph_entity_state", "graph_entity_not_found", "graph_merge_invalid"];

export const GRAPH_PARAM = { q: "q", type: "type", status: "status", sort: "sort", page: "page" } as const;

export const ENTITY_STATUS_FILTERS = ["active", "hidden", "merged", "all"] as const satisfies readonly EntityStatusFilter[];
export const ENTITY_SORTS = ["mentions", "name", "recent"] as const satisfies readonly EntitySort[];
export const DEFAULT_STATUS: EntityStatusFilter = "active";
export const DEFAULT_SORT: EntitySort = "mentions";

/** Icon, tone and label key of each extraction run status. */
export const RUN_LOOK = {
  queued: { icon: "hourglass", tone: "muted" },
  running: { icon: "refresh-cw", tone: "info" },
  succeeded: { icon: "check-circle", tone: "success" },
  failed: { icon: "alert-triangle", tone: "danger" },
  deferred: { icon: "pause-circle", tone: "warning" },
} as const satisfies Record<string, { icon: IconName; tone: "muted" | "info" | "success" | "danger" | "warning" }>;

/** The run error of a deferral for the size limit (backend `GraphErrorCode.SIZE_LIMIT`): read as a pause, not a failure. */
export const SIZE_LIMIT_CODE = "graph_size_limit";
